import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Table } from 'reactstrap';
import { TextFormat, Translate, getSortState } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons';
import { APP_DATE_FORMAT } from 'app/config/constants';
import { ASC, DESC } from 'app/shared/util/pagination.constants';
import { overrideSortStateWithQueryParams } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities } from './signing-session.reducer';

export const SigningSession = () => {
  const dispatch = useAppDispatch();

  const pageLocation = useLocation();
  const navigate = useNavigate();

  const [sortState, setSortState] = useState(
    overrideSortStateWithQueryParams(getSortState(pageLocation, 'id'), pageLocation.search),
  );

  const signingSessionList = useAppSelector(state => state.signingSession.entities);
  const loading = useAppSelector(state => state.signingSession.loading);

  const getAllEntities = () => {
    dispatch(
      getEntities({
        sort: `${sortState.sort},${sortState.order}`,
      }),
    );
  };

  const sortEntities = () => {
    getAllEntities();
    const endURL = `?sort=${sortState.sort},${sortState.order}`;
    if (pageLocation.search !== endURL) {
      navigate(`${pageLocation.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    sortEntities();
  }, [sortState.order, sortState.sort]);

  const sort = p => () => {
    setSortState({
      ...sortState,
      order: sortState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handleSyncList = () => {
    sortEntities();
  };

  const getSortIconByFieldName = (fieldName: string) => {
    const sortFieldName = sortState.sort;
    const order = sortState.order;
    if (sortFieldName !== fieldName) {
      return faSort;
    }
    return order === ASC ? faSortUp : faSortDown;
  };

  return (
    <div>
      <h2 id="signing-session-heading" data-cy="SigningSessionHeading">
        <Translate contentKey="crawlerBackendApp.signingSession.home.title">Signing Sessions</Translate>
        <div className="d-flex justify-content-end">
          <Button className="me-2" color="info" onClick={handleSyncList} disabled={loading}>
            <FontAwesomeIcon icon="sync" spin={loading} />{' '}
            <Translate contentKey="crawlerBackendApp.signingSession.home.refreshListLabel">Refresh List</Translate>
          </Button>
          <Link
            to="/signing-session/new"
            className="btn btn-primary jh-create-entity"
            id="jh-create-entity"
            data-cy="entityCreateButton"
          >
            <FontAwesomeIcon icon="plus" />
            &nbsp;
            <Translate contentKey="crawlerBackendApp.signingSession.home.createLabel">
              Create new Signing Session
            </Translate>
          </Link>
        </div>
      </h2>
      <div className="table-responsive">
        {signingSessionList && signingSessionList.length > 0 ? (
          <Table responsive>
            <thead>
              <tr>
                <th className="hand" onClick={sort('id')}>
                  <Translate contentKey="crawlerBackendApp.signingSession.id">ID</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('id')} />
                </th>
                <th className="hand" onClick={sort('uuid')}>
                  <Translate contentKey="crawlerBackendApp.signingSession.uuid">Uuid</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('uuid')} />
                </th>
                <th className="hand" onClick={sort('documentType')}>
                  <Translate contentKey="crawlerBackendApp.signingSession.documentType">Document Type</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('documentType')} />
                </th>
                <th className="hand" onClick={sort('documentReferenceId')}>
                  <Translate contentKey="crawlerBackendApp.signingSession.documentReferenceId">
                    Document Reference Id
                  </Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('documentReferenceId')} />
                </th>
                <th className="hand" onClick={sort('documentPath')}>
                  <Translate contentKey="crawlerBackendApp.signingSession.documentPath">Document Path</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('documentPath')} />
                </th>
                <th className="hand" onClick={sort('signedDocumentPath')}>
                  <Translate contentKey="crawlerBackendApp.signingSession.signedDocumentPath">
                    Signed Document Path
                  </Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('signedDocumentPath')} />
                </th>
                <th className="hand" onClick={sort('documentHash')}>
                  <Translate contentKey="crawlerBackendApp.signingSession.documentHash">Document Hash</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('documentHash')} />
                </th>
                <th className="hand" onClick={sort('status')}>
                  <Translate contentKey="crawlerBackendApp.signingSession.status">Status</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('status')} />
                </th>
                <th className="hand" onClick={sort('createdAt')}>
                  <Translate contentKey="crawlerBackendApp.signingSession.createdAt">Created At</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('createdAt')} />
                </th>
                <th className="hand" onClick={sort('updatedAt')}>
                  <Translate contentKey="crawlerBackendApp.signingSession.updatedAt">Updated At</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('updatedAt')} />
                </th>
                <th>
                  <Translate contentKey="crawlerBackendApp.signingSession.customer">Customer</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th>
                  <Translate contentKey="crawlerBackendApp.signingSession.contact">Contact</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {signingSessionList.map((signingSession, i) => (
                <tr key={`entity-${i}`} data-cy="entityTable">
                  <td>
                    <Button tag={Link} to={`/signing-session/${signingSession.id}`} color="link" size="sm">
                      {signingSession.id}
                    </Button>
                  </td>
                  <td>{signingSession.uuid}</td>
                  <td>
                    <Translate contentKey={`crawlerBackendApp.DocumentType.${signingSession.documentType}`} />
                  </td>
                  <td>{signingSession.documentReferenceId}</td>
                  <td>{signingSession.documentPath}</td>
                  <td>{signingSession.signedDocumentPath}</td>
                  <td>{signingSession.documentHash}</td>
                  <td>
                    <Translate contentKey={`crawlerBackendApp.SigningSessionStatus.${signingSession.status}`} />
                  </td>
                  <td>
                    {signingSession.createdAt ? (
                      <TextFormat type="date" value={signingSession.createdAt} format={APP_DATE_FORMAT} />
                    ) : null}
                  </td>
                  <td>
                    {signingSession.updatedAt ? (
                      <TextFormat type="date" value={signingSession.updatedAt} format={APP_DATE_FORMAT} />
                    ) : null}
                  </td>
                  <td>
                    {signingSession.customer ? (
                      <Link to={`/customer/${signingSession.customer.id}`}>{signingSession.customer.id}</Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td>
                    {signingSession.contact ? (
                      <Link to={`/contact/${signingSession.contact.id}`}>{signingSession.contact.id}</Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td className="text-end">
                    <div className="btn-group flex-btn-group-container">
                      <Button
                        tag={Link}
                        to={`/signing-session/${signingSession.id}`}
                        color="info"
                        size="sm"
                        data-cy="entityDetailsButton"
                      >
                        <FontAwesomeIcon icon="eye" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.view">View</Translate>
                        </span>
                      </Button>
                      <Button
                        tag={Link}
                        to={`/signing-session/${signingSession.id}/edit`}
                        color="primary"
                        size="sm"
                        data-cy="entityEditButton"
                      >
                        <FontAwesomeIcon icon="pencil-alt" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.edit">Edit</Translate>
                        </span>
                      </Button>
                      <Button
                        onClick={() => (window.location.href = `/signing-session/${signingSession.id}/delete`)}
                        color="danger"
                        size="sm"
                        data-cy="entityDeleteButton"
                      >
                        <FontAwesomeIcon icon="trash" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.delete">Delete</Translate>
                        </span>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          !loading && (
            <div className="alert alert-warning">
              <Translate contentKey="crawlerBackendApp.signingSession.home.notFound">
                No Signing Sessions found
              </Translate>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default SigningSession;
